import { Box } from '@dtx-company/design-system/src/components/Box/Box'
import { Button, ButtonProps } from '@dtx-company/design-system/src/components/Button/Button'
import { FC, ReactNode } from 'react'
import { Stack } from '@dtx-company/design-system/src/components/Stack/Stack'
import { styled } from '@mui/system'
import Link, { LinkProps } from 'next/link'
import Typography from '@mui/material/Typography'

type ButtonSizeVariant = 'sm' | 'lg'

export interface PickerButtonProps {
  hasValue: boolean
  startIcon?: JSX.Element
  buttonStartIcon: ButtonProps['startIcon']
  endIcon?: JSX.Element
  endIconCustomAriaLabel?: string
  displayValue?: string
  label: string
  onClick: () => void
  removeClickHandler?: () => void
  'data-testid'?: string
  variant?: ButtonSizeVariant
  disabled?: ButtonProps['disabled']
  href?: LinkProps['href']
  children?: ReactNode
}

export const InputAdornmentIconButton = styled('button')(({ theme }) => ({
  appearance: 'button',
  border: 'none',
  background: 'none',
  padding: 0,
  margin: 0,
  borderRadius: '50%',
  height: theme.spacing(5),
  width: theme.spacing(5),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer'
}))

export const PickerButton: FC<PickerButtonProps> = ({
  hasValue,
  startIcon,
  buttonStartIcon,
  endIcon,
  endIconCustomAriaLabel,
  children,
  displayValue,
  label,
  onClick,
  removeClickHandler,
  'data-testid': dataTestId,
  variant = 'lg',
  disabled,
  href
}) => {
  const isLink = Boolean(href)

  return (
    <Box
      data-testid={dataTestId}
      sx={theme => ({
        display: 'flex',
        minWidth: hasValue ? 0 : 'fit-content',
        'button, a': {
          boxShadow: 'none',
          height: variant === 'lg' ? theme.spacing(14) : theme.spacing(10),
          fontSize: theme.spacing(4),
          minWidth: 0,
          whiteSpace: 'nowrap'
        }
      })}
    >
      {children}
      {hasValue ? (
        <Stack
          direction="row"
          gap={1.5}
          sx={{
            alignItems: 'center',
            bgcolor: 'secondary.main',
            px: 4,
            minWidth: 0,
            width: '100%',
            'svg, button': {
              flexShrink: 0
            }
          }}
        >
          {startIcon}
          <Typography
            sx={{
              textAlign: 'left',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {displayValue}
          </Typography>
          {endIcon && (
            <InputAdornmentIconButton
              type="button"
              onClick={removeClickHandler || onClick}
              aria-label={endIconCustomAriaLabel || 'Remove'}
              sx={{ ml: 'auto' }}
            >
              {endIcon}
            </InputAdornmentIconButton>
          )}
        </Stack>
      ) : (
        <>
          {isLink && (
            <Link href={href ?? ''} passHref legacyBehavior>
              <Button
                startIcon={buttonStartIcon}
                color="secondary"
                onClick={onClick}
                fullWidth
                disabled={disabled}
                component="a"
                data-testid={`${label} link`}
              >
                {label}
              </Button>
            </Link>
          )}
          {!isLink && (
            <Button
              startIcon={buttonStartIcon}
              color="secondary"
              onClick={onClick}
              fullWidth
              disabled={disabled}
            >
              {label}
            </Button>
          )}
        </>
      )}
    </Box>
  )
}
